/**
 * RPDM W1-D · Project Settings Snapshot (read-only, super_admin only).
 *
 * SCOPE: Renders a read-only snapshot of the Project Type definition
 * + rules resolved for this engagement. Uses the existing RPDM config
 * `/api/rpdm/config/resolve` endpoint (W1-A) — no new backend surface.
 *
 * NO write. NO forms. NO designer. NO visual editor. Purely a display
 * of what the RPDM configuration resolver returns for the engagement's
 * project type.
 *
 * Route: /projects/:id/settings (super_admin only, gated on
 *        master + settings flags)
 */
import { useEffect, useState } from "react";
import ProjectWorkspaceShell from "./ProjectWorkspaceShell";
import WorkspaceWidgetShell from "./WorkspaceWidgetShell";
import { api, formatApiError } from "../../../lib/api";
import { toast } from "sonner";

async function _resolveKey(configurationKey, scope) {
    const params = new URLSearchParams();
    params.set("configuration_key", configurationKey);
    Object.entries(scope || {}).forEach(([k, v]) => {
        if (v != null && v !== "") params.set(k, String(v));
    });
    const r = await api.get(`/rpdm/config/resolve?${params.toString()}`);
    return r.data;
}

export default function ProjectSettingsSnapshot() {
    return (
        <ProjectWorkspaceShell>
            {(view) => (
                <SettingsBody view={view} />
            )}
        </ProjectWorkspaceShell>
    );
}

function SettingsBody({ view }) {
    const kind = view.kind || "internal";
    const [defn, setDefn] = useState(null);
    const [rules, setRules] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setErr(null);
            try {
                const [d, r] = await Promise.all([
                    _resolveKey(`project_type.${kind}.definition`, { scope_type: "platform" }),
                    _resolveKey(`project_type.${kind}.rules`, { scope_type: "platform" }),
                ]);
                if (!cancelled) {
                    setDefn(d);
                    setRules(r);
                }
            } catch (e) {
                if (!cancelled) {
                    setErr(formatApiError(e));
                    toast.error(formatApiError(e));
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [kind]);

    return (
        <div className="mx-auto max-w-6xl space-y-5" data-testid="rpdm-settings-page">
            <WorkspaceWidgetShell
                title={`Project Type · ${kind}`}
                description="Snapshot of the RPDM configuration resolved for this engagement. Read-only in W1-D."
                testId="rpdm-widget-settings-project-type"
                footer={
                    loading
                        ? "Loading configuration…"
                        : defn
                        ? `configuration_id: ${defn.configuration_id} · version: ${defn.version}`
                        : "no snapshot"
                }
            >
                {err ? (
                    <p className="text-xs text-rose-600" data-testid="rpdm-settings-error">
                        {err}
                    </p>
                ) : loading ? (
                    <p className="text-xs text-slate-500">Loading…</p>
                ) : (
                    <SnapshotDump payload={defn?.payload} testId="rpdm-settings-definition" />
                )}
            </WorkspaceWidgetShell>

            <WorkspaceWidgetShell
                title={`Project Type · ${kind} · Rules`}
                description="Engagement-binding rules and downstream constraints. Read-only in W1-D."
                testId="rpdm-widget-settings-rules"
                footer={
                    loading
                        ? "Loading configuration…"
                        : rules
                        ? `configuration_id: ${rules.configuration_id} · version: ${rules.version}`
                        : "no snapshot"
                }
            >
                {loading ? (
                    <p className="text-xs text-slate-500">Loading…</p>
                ) : (
                    <SnapshotDump payload={rules?.payload} testId="rpdm-settings-rules" />
                )}
            </WorkspaceWidgetShell>
        </div>
    );
}

function SnapshotDump({ payload, testId }) {
    if (!payload) {
        return (
            <p className="text-xs text-slate-500" data-testid={`${testId}-empty`}>
                No payload resolved.
            </p>
        );
    }
    return (
        <pre
            data-testid={testId}
            className="max-h-[420px] overflow-auto rounded-md bg-slate-900 p-3 font-mono text-[11px] leading-relaxed text-slate-100"
        >
            {JSON.stringify(payload, null, 2)}
        </pre>
    );
}
